import { Location } from "@angular/common";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import localforage from "localforage";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  delay,
  distinctUntilChanged,
  filter,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from "rxjs";

import { SSO } from "constants/notifications.constants";
import { LoginMethod, LogoutMethod } from "features/amplitude/models/amplitude.model";
import { AmplitudeService } from "features/amplitude/services/amplitude.service";
import Auth, {
  PasswordLoginCredentials,
  SSOCredentials,
  TwoFALoginCredentials,
} from "models/Auth";
import Organization from "models/Organization";
import { LOGIN_SUCCESS } from "screens/login/constants/notifications.constants";
import { AuthService } from "services/auth.service";
import { NotificationService } from "services/notification.service";
import { ReportService } from "services/report.service";
import { SubDomainService } from "services/sub-domain.service";
import { User, UserDto } from "../models/User";
import { OrgFacade } from "./org.facade";
import { UserFacade } from "./user.facade";

export interface AuthState {
  user: User | null;
  org: Organization | null;
}

@Injectable({
  providedIn: "root",
})
export class AuthFacade {
  /* Inject dependencies explicitly to facilitate maintenance of org scoped providers */
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly subDomainService = inject(SubDomainService);
  private readonly userFacade = inject(UserFacade);
  private readonly orgFacade = inject(OrgFacade);
  private readonly notificationService = inject(NotificationService);
  private readonly reportService = inject(ReportService);
  private readonly amplitudeService = inject(AmplitudeService);
  /* State */

  private _rememberMeEmail = new BehaviorSubject<string | null>(null);

  /* Public API: Observable methods */

  public login$(
    credentials: PasswordLoginCredentials
  ): Observable<{ success: boolean; shouldSwitchTo2FA?: boolean }> {
    return of(credentials).pipe(
      switchMap(({ email, password, rememberMe }: PasswordLoginCredentials) =>
        this.authService.login(email, password).pipe(
          tap(() => {
            if (rememberMe !== undefined) {
              /* don't await */ this.updateRememberMeEmail(rememberMe ? email : null);
            }
          })
        )
      ),
      switchMap(({ body: auth }) => this.onLoginSuccess$(auth)),
      map(() => ({ success: true })),
      catchError((error) => {
        const shouldSwitchTo2FA =
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.Unauthorized &&
          error.error.includes("2 Factor Authentication");
        return of({ success: false, shouldSwitchTo2FA });
      })
    );
  }

  public login2FA$(credentials: TwoFALoginCredentials): Observable<{ success: boolean }> {
    return of(credentials).pipe(
      switchMap(({ email, password, code }: TwoFALoginCredentials) =>
        this.authService.login2FA(email, password, code)
      ),
      switchMap(({ body: auth }) => this.onLoginSuccess$(auth)),
      map(() => ({ success: true })),
      catchError(() => of({ success: false }))
    );
  }

  public authenticateOkta$(
    subDomainName: string,
    destinationPath?: string
  ): Observable<{ success: boolean }> {
    return of({ subDomainName, destinationPath }).pipe(
      switchMap(({ subDomainName, destinationPath }) =>
        this.authService.authenticateOkta(subDomainName, destinationPath)
      ),
      map((oktaResponse) => ({
        redirectUrl: oktaResponse?.redirect_uri,
      })),
      switchMap(({ redirectUrl }) => {
        if (redirectUrl) {
          // Redirect to external login page
          this.notificationService.displaySuccess(
            LOGIN_SUCCESS.OKTA_REDIRECT_TO_EXTERNAL_LOGIN
          );
          return of({ success: true }).pipe(
            delay(3000),
            tap(() => {
              window.location.href = redirectUrl;
            })
          );
        }
      }),
      catchError((error) => this.onOktaFailure$(error))
    );
  }

  public loginOkta$(
    code: string,
    subDomainName?: string
  ): Observable<{ success: boolean }> {
    return of({ code, subDomainName }).pipe(
      switchMap(({ code, subDomainName }) =>
        this.authService.loginOkta(code, subDomainName)
      ),
      switchMap(({ body: auth }) => this.onLoginSuccess$(auth)),
      map(() => ({ success: true })),
      catchError((error) => this.onOktaFailure$(error))
    );
  }

  public findSubDomain$(organization: string): Observable<{ redirectUrl: string }> {
    return this.authService.findSubDomain(organization).pipe(
      tap((response) => {
        if (response && response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else {
          console.error("No redirect URL found in the response");
        }
      }),
      catchError((error) => {
        console.error("Error finding subdomain:", error);
        window.location.href = this.subDomainService.fallbackLoginUrl;
        return throwError(() => error);
      })
    );
  }

  public loginExternalClient$(code: string): Observable<boolean> {
    return of({ code }).pipe(
      switchMap(({ code }) => this.authService.loginExternalClient(code)),
      map(({ body }) => body),
      switchMap(({ redirectReportId }) =>
        this.onExternalUserLoginSuccess$(redirectReportId)
      ),
      map(() => true),
      catchError(() => {
        this.notificationService.displayError(SSO.INCORRECT_CODE);
        return of(false);
      })
    );
  }

  public loginSSO$(credentials: SSOCredentials): Observable<boolean> {
    return of(credentials).pipe(
      switchMap(({ code, clientId, redirectUrl }: SSOCredentials) =>
        this.authService.authenticateSAML(code, clientId, redirectUrl)
      ),
      switchMap(({ body: auth }) => this.onLoginSuccess$(auth)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  private onLoginSuccess$(auth: Auth): Observable<AuthState> {
    return of(auth).pipe(
      map((auth) => this.setUser(auth)),
      filter((user): user is User => user !== null),
      switchMap((user) =>
        this.orgFacade.org$.pipe(
          filter((org): org is Organization => org !== null),
          take(1),
          switchMap((org) => {
            let { destinationPath } = this.activatedRoute.snapshot.queryParams;

            destinationPath = destinationPath?.startsWith("/login")
              ? undefined
              : destinationPath;

            const postLoginRedirectUrl = user.checkUrlPermissions(destinationPath);

            // Replace the login entry so it's excluded from history (no back-to-login).
            /* don't await */
            // Await navigation to the post-login redirect URL
            return from(
              this.router.navigateByUrl(postLoginRedirectUrl, {
                replaceUrl: true,
              })
            ).pipe(map(() => ({ user, org })));
          }),
          tap(({ user, org }) => {
            this.amplitudeService.initializeAmplitudeUser(
              user,
              org,
              auth?.isSSOUser ? LoginMethod.sso : LoginMethod.email_password
            );
          })
        )
      )
    );
  }

  private onExternalUserLoginSuccess$(redirectReportId: string): Observable<AuthState> {
    return this.fetchUser$().pipe(
      filter((user): user is User => user !== null),
      // The org is fetched reactively by OrgFacade once the user is set;
      // wait for it here since the comparison-reports queue needs it.
      switchMap((user) =>
        this.orgFacade.org$.pipe(
          filter((org): org is Organization => org !== null),
          take(1),
          map((org) => ({ user, org }))
        )
      ),
      tap(async ({ user, org }) => {
        this.amplitudeService.initializeAmplitudeUser(
          user,
          org,
          LoginMethod.external_api
        );

        const postLoginRedirectUrl = `/report?reportId=${redirectReportId}`;
        // Replace the login entry so it's excluded from history (no back-to-login).
        await this.router.navigateByUrl(postLoginRedirectUrl, { replaceUrl: true });
        this.reportService.processComparisonReportsQueue(org._id, user);
      })
    );
  }

  private onOktaFailure$(error: unknown): Observable<{ success: boolean }> {
    if (
      error instanceof HttpErrorResponse &&
      error.status === HttpStatusCode.Unauthorized
    ) {
      // Wait for a few seconds, then redirect to standard login page
      return of({ success: false }).pipe(
        delay(3000),
        tap(() => {
          window.location.href = this.subDomainService.fallbackLoginUrl;
        })
      );
    }

    return throwError(() => error);
  }

  /**
   * @param returnToCurrentUrl whether, after successful login, the user should be
   * redirected to the URL at which the logout was triggered
   */
  public logout$(returnToCurrentUrl: boolean = false): Observable<boolean> {
    if (returnToCurrentUrl) {
      this.amplitudeService.trackUserLoggedOut(LogoutMethod.timeout);
    } else {
      this.amplitudeService.trackUserLoggedOut(LogoutMethod.manual);
    }
    const subDomainName = this.subDomainService.subDomain || null;
    const destinationPath = returnToCurrentUrl ? this.location.path() : undefined;
    return of({ subDomainName, destinationPath }).pipe(
      switchMap(({ subDomainName, destinationPath }) => {
        const logoutOptions = subDomainName
          ? { subDomainName, destinationPath }
          : undefined;

        return this.authService.logout(logoutOptions);
      }),
      map((response) => response?.logoutUrl),
      tap((logoutUrl) => {
        if (logoutUrl) {
          this.amplitudeService.flush();
          // Changing URL will stop further execution of the pipeline,
          // so we need to do the cleanup before it
          /* don't await */ localforage.setItem("organization", null);
          window.location.href = logoutUrl;
        }
      }),
      switchMap(() => {
        const navigationOptions: NavigationExtras = {};
        if (destinationPath) {
          navigationOptions.queryParams = { destinationPath };
        }
        this.amplitudeService.flush();
        return from(this.router.navigate(["/login"], navigationOptions));
      }),
      tap(() => this.orgFacade.setOrg(null)),
      tap(() => this.userFacade.setUser(null)),
      map(() => true),
      catchError(() => {
        return of(false).pipe(
          tap(() => {
            window.location.href = this.subDomainService.fallbackLoginUrl;
          })
        );
      })
    );
  }

  public restoreRememberMeEmail$(): Observable<string | null> {
    return of(undefined).pipe(
      switchMap(() => from(localforage.getItem<string | null>("inseer-remember-me"))),
      tap((email) => {
        this._rememberMeEmail.next(email);
      })
    );
  }

  public updateRememberMeEmail$(email: string | null): Observable<string | null> {
    return of(email).pipe(
      tap((email) => {
        this._rememberMeEmail.next(email);
      }),
      switchMap((email) =>
        from(localforage.setItem("inseer-remember-me", email)).pipe(map(() => email))
      )
    );
  }

  public clearRememberMeIfConsentRevoked(consentFunctional: boolean): void {
    if (!consentFunctional) {
      this.updateRememberMeEmail(null);
    }
  }

  public fetchUser$(): Observable<User | null> {
    return of(undefined).pipe(
      switchMap(() => this.authService.getUser()),
      map((userDto) => this.setUser(userDto))
    );
  }

  public setUser(userDto: UserDto | null): User | null {
    const user = userDto ? new User(userDto) : null;
    this.userFacade.setUser(user);
    return user;
  }

  /* Public API: Dispatch-and-forget methods */

  public login(credentials: PasswordLoginCredentials): void {
    this.login$(credentials).subscribe();
  }

  public login2FA(credentials: TwoFALoginCredentials): void {
    this.login2FA$(credentials).subscribe();
  }

  public authenticateOkta(subDomainName: string, destinationPath?: string): void {
    this.authenticateOkta$(subDomainName, destinationPath).subscribe();
  }

  public loginOkta(code: string, subDomainName?: string): void {
    this.loginOkta$(code, subDomainName).subscribe();
  }

  public findSubDomain(organization: string): void {
    this.findSubDomain$(organization).subscribe();
  }

  public loginExternalClient(code: string): void {
    this.loginExternalClient$(code).subscribe();
  }

  public loginSSO(credentials: SSOCredentials): void {
    this.loginSSO$(credentials).subscribe();
  }

  /**
   * @param returnToCurrentUrl whether, after successful login, the user should be
   * redirected to the URL at which the logout was triggered
   */
  public logout(returnToCurrentUrl: boolean = false): void {
    this.logout$(returnToCurrentUrl).subscribe();
  }

  public restoreRememberMeEmail(): void {
    this.restoreRememberMeEmail$().subscribe();
  }

  public updateRememberMeEmail(email: string | null): void {
    this.updateRememberMeEmail$(email).subscribe();
  }

  public fetchUser(): void {
    this.fetchUser$().subscribe();
  }

  /* State initializers */

  private initializeUser$: Observable<User | null> = defer(() => this.fetchUser$()).pipe(
    shareReplay(1)
  );

  private initializeRememberMeEmail$: Observable<string | null> = defer(() =>
    this.restoreRememberMeEmail$()
  ).pipe(shareReplay(1));

  /* Public API: Observable state selectors */

  public readonly user$: Observable<User | null> = this.initializeUser$.pipe(
    switchMap(() => this.userFacade.user$)
  );

  public readonly org$: Observable<Organization | null> = this.orgFacade.org$;

  public readonly orgId$: Observable<string | null> = this.org$.pipe(
    map((org) => this.orgFacade.getOrgId(org))
  );

  public readonly state$: Observable<AuthState> = combineLatest([
    this.user$,
    this.org$,
  ]).pipe(map(([user, org]) => ({ user, org })));

  public readonly isAuthenticated$: Observable<boolean> = this.userFacade.isUserDefined$;

  public readonly rememberMeEmail$: Observable<string | null> =
    this.initializeRememberMeEmail$.pipe(
      switchMap(() => this._rememberMeEmail.asObservable()),
      distinctUntilChanged()
    );
}
